import { isNotNullish, isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { sidebarConfig } from './../../modules/gojs-sidebar/config/config';
import { withoutUndoManager } from './../../utils/without-undo-manager';
import { DRAG_CREATING_TOOL_NAME } from './config';

export class DragCreatingTool extends go.Tool {
  constructor() {
    super();
    this.name = DRAG_CREATING_TOOL_NAME;
    this.isEnabled = false;
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  private box: go.Part | null = null;
  private oldDefaultCursor?: {
    diagram: string;
    document: string;
  };
  private overlay?: HTMLDivElement;
  private readonly defaultShapeProperties: go.ObjectData = {
    figure: 'Rectangle',
    fill: null,
    stroke: 'magenta',
  };

  cursor = '';
  insertAction: ((bounds: go.Rect) => void) | null = null;
  shapeProperties: go.ObjectData = this.defaultShapeProperties;
  allowedClickClass = '';

  canStart() {
    if (!this.isEnabled) {
      return false;
    }
    if (isNullish(this.diagram)) {
      return false;
    }
    if (this.diagram.isReadOnly || this.diagram.isModelReadOnly) {
      return false;
    }
    if (!this.diagram.allowInsert) {
      return false;
    }
    if (!this.diagram.lastInput.left) {
      return false;
    }

    return true;
  }

  startManually(insertAction: (bounds: go.Rect) => void) {
    this.diagram.currentTool = this;
    this.isEnabled = true;
    this.insertAction = insertAction;
    this.doActivate();
  }

  doActivate() {
    super.doActivate();
    this.diagram.isMouseCaptured = true;
    this.oldDefaultCursor = {
      diagram: this.diagram.defaultCursor,
      document: document.body.style.cursor,
    };
    this.diagram.defaultCursor = this.cursor;
    document.body.style.cursor = this.cursor;
    this.addOverlay();
    document.addEventListener('keydown', this.onKeyDown);
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.doCancel();
    }
  }

  doMouseDown() {
    if (isNullish(this.diagram) || !this.isAllowedClick() || !this.isActive) {
      return;
    }
    const { position } = this.computeBoxBounds();
    const box = this.createBox();
    box.position = position;
    this.diagram.add(box);
  }

  doDeactivate() {
    super.doDeactivate();
    if (isNullish(this.diagram)) {
      return;
    }
    withoutUndoManager(this.diagram, () => {
      if (this.box) {
        this.diagram.remove(this.box);
      }
      if (this.oldDefaultCursor) {
        document.body.style.cursor = this.oldDefaultCursor.document;
        this.diagram.defaultCursor = this.oldDefaultCursor.diagram;
      }
      this.diagram.isMouseCaptured = false;
      this.isActive = false;
      this.isEnabled = false;

      this.shapeProperties = this.defaultShapeProperties;
      this.insertAction = null;
      this.removeOverlay();

      document.removeEventListener('keydown', this.onKeyDown);
    });
  }

  doMouseMove() {
    if (isNullish(this.diagram) || !this.isActive || isNullish(this.box)) {
      return;
    }
    const { size, position } = this.computeBoxBounds();
    const shape = this.box.findMainElement();
    if (shape) {
      shape.desiredSize = size;
    }
    this.box.position = position;
  }

  doMouseUp() {
    if (this.isActive && isNotNullish(this.box)) {
      const rect = this.box.findMainElement()?.getDocumentBounds();
      if (rect) {
        rect.width =
          rect.width < sidebarConfig.dragCreateTool.minSize ? sidebarConfig.dragCreateTool.defaultSize : rect.width;
        rect.height =
          rect.height < sidebarConfig.dragCreateTool.minSize ? sidebarConfig.dragCreateTool.defaultSize : rect.height;
      }
      if (this.insertAction && rect) {
        this.insertAction(rect);
      }
      this.diagram.remove(this.box);
      this.box = null;
    }
    this.stopTool();
  }

  private addOverlay() {
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.style.width = '100%';
      this.overlay.style.height = '100%';
      this.overlay.style.zIndex = '99999';
      this.overlay.style.position = 'fixed';
      this.overlay.style.top = '0';
      this.overlay.style.left = '0';
    }

    this.overlay.addEventListener('click', this.doDeactivate);
    this.diagram.div?.appendChild(this.overlay);
  }

  private removeOverlay() {
    this.overlay?.removeEventListener('click', this.doDeactivate);
    if (this.overlay?.parentElement === this.diagram.div) {
      this.diagram.div?.removeChild(this.overlay);
    } else if (this.overlay?.remove) {
      this.overlay.remove();
    }
  }

  private isAllowedClick() {
    const { div, lastInput } = this.diagram;
    const clickedOnCanvas = lastInput?.event?.target instanceof Node && div?.contains(lastInput.event.target);
    const clickedOnAllowedClass =
      isNotNullish(this.allowedClickClass) &&
      lastInput.event?.target instanceof Element &&
      lastInput.event.target.classList.contains(this.allowedClickClass);

    return clickedOnCanvas || clickedOnAllowedClass;
  }

  private createBox() {
    const $ = go.GraphObject.make;
    const box = $(
      go.Part,
      {
        layerName: 'Tool',
        selectable: false,
      },
      $(go.Shape, {
        name: 'SHAPE',
        position: new go.Point(0, 0),
        desiredSize: new go.Size(0, 0),
        ...this.shapeProperties,
      }),
    );

    this.box = box;
    return box;
  }

  private computeBoxBounds(): go.Rect {
    const start = this.diagram.firstInput.documentPoint;
    const latest = this.diagram.lastInput.documentPoint;

    return new go.Rect(start, latest);
  }
}
